import { initializeApp, getApps, getApp } from 'firebase/app';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import {
  initializeAuth,
  getAuth,
  GoogleAuthProvider,
  connectAuthEmulator,
  browserLocalPersistence,
  indexedDBLocalPersistence,
  browserPopupRedirectResolver,
} from 'firebase/auth';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { getInstallations, deleteInstallations } from 'firebase/installations';
import { getStorage } from 'firebase/storage';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

const provider = new GoogleAuthProvider();
provider.addScope('https://www.googleapis.com/auth/contacts.readonly');

// In emulator mode, override the projectId to match the Firebase Admin SDK (gestion-fc),
// which is the emulator's configured project. This ensures the same Firestore namespace
// is used by both the Admin SDK (in tests) and the React client.
const resolvedConfig =
  process.env.REACT_APP_USE_EMULATORS === 'true'
    ? { ...firebaseConfig, projectId: 'gestion-fc' }
    : firebaseConfig;

const app = getApps().length ? getApp() : initializeApp(resolvedConfig);
const db = getFirestore(app);
const storage = getStorage(app);

// initializeAuth sets persistence synchronously at creation time.
// In emulator mode we force browserLocalPersistence so Playwright storageState
// (which captures localStorage but not indexedDB) can save/restore auth tokens.
// HMR guard: initializeAuth throws if called twice on the same app — fall back to getAuth().
// initializeAuth throws if called twice on the same app (HMR), so fall back to getAuth().
// eslint-disable-next-line import/no-mutable-exports
const auth = (() => {
  try {
    const a = initializeAuth(app, {
      persistence:
        process.env.REACT_APP_USE_EMULATORS === 'true'
          ? browserLocalPersistence
          : [indexedDBLocalPersistence, browserLocalPersistence],
      popupRedirectResolver: browserPopupRedirectResolver,
    });
    return a;
  } catch (_) {
    return getAuth(app);
  }
})();
auth.useDeviceLanguage();

// Connect to local emulators when running E2E tests
if (process.env.REACT_APP_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, 'localhost', 8080);
}

// Firebase Messaging is NOT supported on iOS Safari (non-installed) nor in
// in-app webviews (Instagram, WhatsApp...): getMessaging() throws synchronously
// there and, at module level, that crashed the whole app into a blank page.
// isSupported() resolves asynchronously; messaging stays null when unavailable.
// eslint-disable-next-line import/no-mutable-exports
let messaging = null;

const messagingInit = isSupported()
  .then(supported => {
    if (!supported) return null;
    messaging = getMessaging(app);

    // Single permanent listener for foreground messages.
    // Chrome suppresses Notification() when a SW is registered, so we use
    // registration.showNotification() — same as the SW does for background
    // messages. Safe for data-only messages: payload.notification may be
    // undefined. Registered once and never accumulates.
    if (process.env.REACT_APP_USE_EMULATORS !== 'true') {
      onMessage(messaging, payload => {
        if (!navigator.serviceWorker?.controller) return;
        const title = payload.notification?.title ?? payload.data?.title ?? 'GameBooking';
        const body = payload.notification?.body ?? payload.data?.body ?? '';
        const link = payload.fcmOptions?.link ?? payload.data?.url ?? '/';
        navigator.serviceWorker.ready.then(reg => {
          reg.showNotification(title, {
            body,
            icon: '/appIcon-192.png',
            badge: '/appIcon-192.png',
            data: { url: link },
          });
        });
      });
    }
    return messaging;
  })
  .catch(() => null);

// The browser's live push subscription lives at this SW scope. FCM registration
// tokens are the last segment of the subscription endpoint URL, so we can use
// them to detect drift between the browser's real subscription and the token
// the Firebase SDK is caching in IndexedDB.
const FCM_SW_SCOPE = '/firebase-cloud-messaging-push-scope';

const getSubscriptionEndpoint = async () => {
  try {
    const reg = await navigator.serviceWorker?.getRegistration(FCM_SW_SCOPE);
    const sub = await reg?.pushManager?.getSubscription();
    return sub?.endpoint ?? null;
  } catch {
    return null;
  }
};

// Wipe local FCM/installation state so the next getToken() re-subscribes cleanly.
// Used as a last-resort self-heal when the SDK's cached token doesn't match what
// Chrome's push service is actually holding.
const resetFcmState = async () => {
  try {
    const reg = await navigator.serviceWorker?.getRegistration(FCM_SW_SCOPE);
    const sub = await reg?.pushManager?.getSubscription();
    if (sub) await sub.unsubscribe();
  } catch (err) {
    console.warn('[FCM] unsubscribe failed:', err?.message);
  }
  try {
    await deleteInstallations(getInstallations(app));
  } catch (err) {
    console.warn('[FCM] deleteInstallations failed:', err?.message);
  }
};

// Returns a fresh FCM token on every call — Firebase SDK caches it internally
// when still valid, or fetches a new one if rotated/expired. Self-heals when
// the SDK's cache diverges from the browser's real push subscription (see the
// endpoint-mismatch branch below). Resolves null on platforms without push
// support.
const getFcmToken = () =>
  process.env.REACT_APP_USE_EMULATORS === 'true'
    ? Promise.resolve(null)
    : messagingInit.then(async m => {
        if (!m) return null;
        const vapidKey = process.env.REACT_APP_FCM_VAPID_KEY;

        const fetchToken = async () => {
          try {
            return await getToken(m, { vapidKey });
          } catch (err) {
            console.error('[FCM] getToken failed:', err?.code, err?.message);
            // A stale/corrupt Firebase Installation in this browser makes FCM
            // registration fail with a 401 (token-subscribe-failed) even though
            // the backend is fine. Delete the installation to force a fresh FID
            // + auth token, then retry once.
            if (err?.code === 'messaging/token-subscribe-failed') {
              try {
                await deleteInstallations(getInstallations(app));
                return await getToken(m, { vapidKey });
              } catch (retryErr) {
                console.error('[FCM] getToken retry failed:', retryErr?.code, retryErr?.message);
              }
            }
            return null;
          }
        };

        const token = await fetchToken();

        // Self-heal: if the browser has a push subscription whose endpoint does
        // NOT end with the token we just got, the SDK's cache is out of sync
        // with what Chrome's push service is holding. Any push we send targets
        // a token that doesn't correspond to this browser and is dropped
        // silently. Unsubscribe + delete installation + re-request forces a
        // clean re-registration whose token matches the live endpoint.
        if (token) {
          const endpoint = await getSubscriptionEndpoint();
          if (endpoint && !endpoint.endsWith(token)) {
            console.warn('[FCM] token/endpoint drift — resetting');
            await resetFcmState();
            return fetchToken();
          }
        }
        return token;
      });

if (navigator.serviceWorker) {
  navigator.serviceWorker
    .register('/firebase-messaging-sw.js')
    .catch(() => {});
}

export { db, auth, storage, messaging, getFcmToken };
